import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, X, Phone, Mail, Calendar, MessageSquare, CheckCircle2, XCircle, RefreshCw, Play, Pause, CreditCard, Building2, User, FileText, Plus, ArrowRight, Lock, Bell, ChevronDown } from 'lucide-react';
import { API_BASE } from '../../config/apiBase';

const API = API_BASE;

const STAGE_CONFIG = {
  lead: { label: 'Lead', color: '#6366f1', bg: 'rgba(99,102,241,0.10)' },
  contato_iniciado: { label: 'Contato Iniciado', color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)' },
  aguardando_docs: { label: 'Aguardando Docs', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  aguardando_aprovacao: { label: 'Aguardando Aprovacao', color: '#f97316', bg: 'rgba(249,115,22,0.10)' },
  trial: { label: 'Trial', color: '#06b6d4', bg: 'rgba(6,182,212,0.10)' },
  ativo: { label: 'Ativo', color: '#22c55e', bg: 'rgba(34,197,94,0.10)' },
  cobranca_pendente: { label: 'Cobranca Pendente', color: '#eab308', bg: 'rgba(234,179,8,0.10)' },
  inadimplente: { label: 'Inadimplente', color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  cancelado: { label: 'Cancelado', color: '#6b7280', bg: 'rgba(107,114,128,0.10)' },
  reativacao: { label: 'Reativacao', color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
};
const STAGES = Object.keys(STAGE_CONFIG);
const TEMP_COLORS = { quente: '#ef4444', morno: '#f59e0b', frio: '#3b82f6' };
const CARD_HEIGHT = 88;
const VISIBLE_CARDS = 15;

function fetchAuth(url, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(url, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
}

// ==================== VIRTUAL LIST ====================
function VirtualColumn({ cards, onCardClick, renderCard }) {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const totalHeight = cards.length * CARD_HEIGHT;

  const startIdx = Math.max(0, Math.floor(scrollTop / CARD_HEIGHT) - 2);
  const endIdx = Math.min(cards.length, Math.ceil((scrollTop + VISIBLE_CARDS * CARD_HEIGHT) / CARD_HEIGHT) + 2);
  const visibleCards = cards.slice(startIdx, endIdx);

  const onScroll = useCallback((e) => setScrollTop(e.target.scrollTop), []);

  // Only virtualize if many cards
  if (cards.length <= VISIBLE_CARDS + 5) {
    return (
      <div className="crm-column-body">
        {cards.map((c) => renderCard(c))}
        {cards.length === 0 && <div className="crm-empty-col">Nenhuma empresa</div>}
      </div>
    );
  }

  return (
    <div className="crm-column-body crm-virtual-scroll" ref={containerRef} onScroll={onScroll}>
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleCards.map((c, i) => (
          <div key={c.empresa_id} style={{ position: 'absolute', top: (startIdx + i) * CARD_HEIGHT, left: 0, right: 0, padding: '0 0 6px 0' }}>
            {renderCard(c)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== KANBAN CARD ====================
function KanbanCard({ card, onClick }) {
  const temp = card.temperatura || 'morno';
  return (
    <div
      data-testid={`crm-card-${card.empresa_id}`}
      className="crm-card"
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('empresa_id', card.empresa_id); e.dataTransfer.setData('from_stage', card.stage); }}
      onClick={() => onClick(card)}
    >
      <div className="crm-card-header">
        <span className="crm-card-name">{card.nome}</span>
        <span className="crm-temp-dot" style={{ background: TEMP_COLORS[temp] || '#f59e0b' }} title={temp} />
      </div>
      {card.email && <div className="crm-card-sub"><Mail size={12} /> {card.email}</div>}
      <div className="crm-card-footer">
        <span className="crm-badge" style={{ background: 'var(--surface-3)', color: 'var(--muted)' }}>{card.plano || 'free'}</span>
        {card.tags?.length > 0 && card.tags.slice(0, 2).map((t, i) => <span key={i} className="crm-badge crm-tag">{t}</span>)}
      </div>
    </div>
  );
}

// ==================== KANBAN COLUMN ====================
function KanbanColumn({ stage, data, onCardClick, onDrop, onLoadMore, search, filterTemp }) {
  const cfg = STAGE_CONFIG[stage];
  const [dragOver, setDragOver] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await onLoadMore(stage);
    setLoadingMore(false);
  };

  const renderCard = (c) => <KanbanCard key={c.empresa_id} card={c} onClick={onCardClick} />;

  return (
    <div
      data-testid={`crm-column-${stage}`}
      className={`crm-column ${dragOver ? 'crm-column-dragover' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const eid = e.dataTransfer.getData('empresa_id'); if (eid) onDrop(eid, stage); }}
    >
      <div className="crm-column-header">
        <span className="crm-column-dot" style={{ background: cfg.color }} />
        <span className="crm-column-title">{cfg.label}</span>
        <span className="crm-column-count">{data.total}</span>
      </div>
      <VirtualColumn cards={data.cards} onCardClick={onCardClick} renderCard={renderCard} />
      {data.has_more && (
        <button className="crm-load-more" data-testid={`crm-load-more-${stage}`} onClick={handleLoadMore} disabled={loadingMore}>
          {loadingMore ? '...' : <><ChevronDown size={12} /> Mais ({data.total - data.cards.length})</>}
        </button>
      )}
    </div>
  );
}

// ==================== NOTIFICATION BELL ====================
function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchAuth(`${API}/api/admin/crm/notifications?limit=20`);
      if (res.ok) { const d = await res.json(); setNotifs(d.notifications); setUnread(d.unread_count); }
    } catch {}
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    await fetchAuth(`${API}/api/admin/crm/notifications/read`, { method: 'POST', body: '{}' });
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="crm-notif-wrapper" ref={panelRef}>
      <button className="crm-btn-sm crm-notif-btn" data-testid="crm-notif-bell" onClick={() => { setOpen(!open); if (!open) load(); }}>
        <Bell size={14} />
        {unread > 0 && <span className="crm-notif-badge" data-testid="crm-notif-count">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="crm-notif-panel" data-testid="crm-notif-panel">
          <div className="crm-notif-header">
            <span>Notificacoes CRM</span>
            {unread > 0 && <button className="crm-notif-mark" onClick={markAllRead} data-testid="crm-notif-mark-read">Marcar lidas</button>}
          </div>
          <div className="crm-notif-list">
            {notifs.length === 0 && <div className="crm-empty" style={{ padding: '16px 12px' }}>Nenhuma notificacao</div>}
            {notifs.map((n) => (
              <div key={n.notification_id} className={`crm-notif-item ${!n.read ? 'crm-notif-unread' : ''}`}>
                <div className="crm-notif-dot" style={{ background: STAGE_CONFIG[n.new_stage]?.color || '#888' }} />
                <div className="crm-notif-body">
                  <span className="crm-notif-name">{n.empresa_name}</span>
                  <span className="crm-notif-change">{n.old_stage_label} → {n.new_stage_label}</span>
                  <span className="crm-notif-meta">{n.changed_by} · {timeAgo(n.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== TIMELINE ITEM ====================
function TimelineItem({ entry }) {
  const icons = {
    status_change: <ArrowRight size={14} />, aprovacao: <CheckCircle2 size={14} style={{ color: '#22c55e' }} />,
    rejeicao: <XCircle size={14} style={{ color: '#ef4444' }} />, mudanca_plano: <CreditCard size={14} style={{ color: '#8b5cf6' }} />,
    trial_inicio: <Play size={14} style={{ color: '#06b6d4' }} />, cancelamento: <Pause size={14} style={{ color: '#6b7280' }} />,
    reativacao: <RefreshCw size={14} style={{ color: '#10b981' }} />, nota_interna: <MessageSquare size={14} style={{ color: '#f59e0b' }} />,
  };
  const det = entry.detalhes || {};
  let desc = entry.evento;
  if (entry.evento === 'status_change') desc = `${det.de || '?'} → ${det.para || '?'}`;
  else if (entry.evento === 'nota_interna') desc = det.texto || 'Nota interna';
  else if (entry.evento === 'mudanca_plano') desc = `Plano: ${det.plano || '?'}`;
  else if (entry.evento === 'trial_inicio') desc = `Trial ${det.dias || 14} dias`;
  else if (entry.evento === 'cancelamento') desc = `Cancelado${det.motivo ? ': ' + det.motivo : ''}`;
  else if (entry.evento === 'rejeicao') desc = `Rejeitado${det.motivo ? ': ' + det.motivo : ''}`;
  const dateStr = entry.created_at ? new Date(entry.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className="crm-timeline-item">
      <div className="crm-timeline-icon">{icons[entry.evento] || <FileText size={14} />}</div>
      <div className="crm-timeline-content">
        <span className="crm-timeline-desc">{desc}</span>
        <span className="crm-timeline-meta">{entry.autor} {dateStr && `· ${dateStr}`}</span>
      </div>
    </div>
  );
}

// ==================== DETAIL DRAWER ====================
function DetailDrawer({ empresaId, gates, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('comercial');
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await fetchAuth(`${API}/api/admin/crm/empresa/${empresaId}`); if (res.ok) setData(await res.json()); } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!gates.comercial && !gates.financeiro) setActiveTab('timeline'); }, [gates]);

  const saveComercial = async (fields) => { setSaving(true); await fetchAuth(`${API}/api/admin/crm/empresa/${empresaId}/comercial`, { method: 'PUT', body: JSON.stringify(fields) }); await load(); setSaving(false); };
  const saveFinanceiro = async (fields) => { setSaving(true); await fetchAuth(`${API}/api/admin/crm/empresa/${empresaId}/financeiro`, { method: 'PUT', body: JSON.stringify(fields) }); await load(); setSaving(false); };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    await fetchAuth(`${API}/api/admin/crm/empresa/${empresaId}/timeline`, { method: 'POST', body: JSON.stringify({ evento: 'nota_interna', detalhes: { texto: noteText.trim() } }) });
    setNoteText(''); await load(); setSaving(false);
  };

  const quickAction = async (acao, extra = {}) => {
    setActionLoading(acao);
    await fetchAuth(`${API}/api/admin/crm/empresa/${empresaId}/acoes`, { method: 'POST', body: JSON.stringify({ acao, ...extra }) });
    await load(); onRefresh?.(); setActionLoading('');
  };

  if (loading && !data) return (<div className="crm-drawer" data-testid="crm-drawer"><div className="crm-drawer-header"><span>Carregando...</span><button onClick={onClose}><X size={18} /></button></div></div>);
  if (!data) return null;

  const { empresa = {}, crm = {}, timeline = [], financeiro = {} } = data;
  const stage = crm.stage || 'lead';
  const stageCfg = STAGE_CONFIG[stage] || STAGE_CONFIG.lead;

  const tabs = [
    { key: 'comercial', label: 'Comercial', gate: 'comercial' },
    { key: 'financeiro', label: 'Financeiro', gate: 'financeiro' },
    { key: 'timeline', label: 'Timeline', gate: 'timeline' },
    { key: 'acoes', label: 'Acoes', gate: 'quick_actions' },
  ];

  return (
    <div className="crm-drawer" data-testid="crm-drawer">
      <div className="crm-drawer-header">
        <div>
          <h3 className="crm-drawer-title">{empresa.name || empresa.nome || 'Empresa'}</h3>
          <span className="crm-stage-badge" style={{ background: stageCfg.bg, color: stageCfg.color, border: `1px solid ${stageCfg.color}33` }}>{stageCfg.label}</span>
        </div>
        <button data-testid="crm-drawer-close" onClick={onClose} className="crm-drawer-close"><X size={18} /></button>
      </div>
      <div className="crm-drawer-info">
        {empresa.email && <div className="crm-info-row"><Mail size={13} /> {empresa.email}</div>}
        {(empresa.whatsapp || crm.whatsapp) && <div className="crm-info-row"><Phone size={13} /> {empresa.whatsapp || crm.whatsapp}</div>}
        <div className="crm-info-row"><CreditCard size={13} /> Plano: <b>{empresa.plan || 'free'}</b></div>
        <div className="crm-info-row"><Calendar size={13} /> Criado: {empresa.created_at ? new Date(empresa.created_at).toLocaleDateString('pt-BR') : '-'}</div>
      </div>
      <div className="crm-tabs">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`crm-tab-${t.key}`} className={`crm-tab ${activeTab === t.key ? 'crm-tab-active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {!gates[t.gate] && <Lock size={11} style={{ marginRight: 3, opacity: 0.5 }} />}{t.label}
          </button>
        ))}
      </div>
      <div className="crm-drawer-body">
        {activeTab === 'comercial' && (gates.comercial ? <ComercialTab crm={crm} onSave={saveComercial} saving={saving} /> : <UpgradeCTA feature="Dados comerciais" />)}
        {activeTab === 'financeiro' && (gates.financeiro ? <FinanceiroTab financeiro={financeiro} empresa={empresa} onSave={saveFinanceiro} saving={saving} /> : <UpgradeCTA feature="Dados financeiros" />)}
        {activeTab === 'timeline' && (gates.timeline ? (
          <div className="crm-section">
            <div className="crm-note-input">
              <textarea data-testid="crm-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Adicionar nota interna..." rows={2} />
              <button data-testid="crm-add-note" onClick={addNote} disabled={saving || !noteText.trim()} className="crm-btn-sm">{saving ? '...' : 'Adicionar'}</button>
            </div>
            <div className="crm-timeline">{timeline.length === 0 && <p className="crm-empty">Nenhum registro</p>}{timeline.map((e, i) => <TimelineItem key={i} entry={e} />)}</div>
          </div>
        ) : <UpgradeCTA feature="Timeline" />)}
        {activeTab === 'acoes' && (gates.quick_actions ? (
          <div className="crm-section crm-actions-grid">
            <ActionBtn label="Aprovar" icon={<CheckCircle2 size={16} />} color="#22c55e" loading={actionLoading === 'aprovar'} onClick={() => quickAction('aprovar')} testId="crm-action-aprovar" />
            <ActionBtn label="Rejeitar" icon={<XCircle size={16} />} color="#ef4444" loading={actionLoading === 'rejeitar'} onClick={() => { const m = prompt('Motivo:'); quickAction('rejeitar', { motivo: m || '' }); }} testId="crm-action-rejeitar" />
            <ActionBtn label="Liberar Plano" icon={<CreditCard size={16} />} color="#8b5cf6" loading={actionLoading === 'liberar_plano'} onClick={() => { const p = prompt('Plano? (start, gestao, pro, enterprise)', 'start'); if (p) quickAction('liberar_plano', { plano: p }); }} testId="crm-action-liberar" />
            <ActionBtn label="Trocar Plano" icon={<RefreshCw size={16} />} color="#6366f1" loading={actionLoading === 'trocar_plano'} onClick={() => { const p = prompt('Novo plano:', 'gestao'); if (p) quickAction('trocar_plano', { plano: p }); }} testId="crm-action-trocar" />
            <ActionBtn label="Ativar Trial" icon={<Play size={16} />} color="#06b6d4" loading={actionLoading === 'ativar_trial'} onClick={() => { const d = prompt('Dias de trial:', '14'); if (d) quickAction('ativar_trial', { dias: parseInt(d) || 14 }); }} testId="crm-action-trial" />
            <ActionBtn label="Cancelar" icon={<Pause size={16} />} color="#6b7280" loading={actionLoading === 'cancelar'} onClick={() => { const m = prompt('Motivo:'); quickAction('cancelar', { motivo: m || '' }); }} testId="crm-action-cancelar" />
            <ActionBtn label="Reativar" icon={<RefreshCw size={16} />} color="#10b981" loading={actionLoading === 'reativar'} onClick={() => quickAction('reativar')} testId="crm-action-reativar" />
          </div>
        ) : <UpgradeCTA feature="Acoes rapidas" />)}
      </div>
    </div>
  );
}

function ActionBtn({ label, icon, color, loading, onClick, testId }) {
  return (<button data-testid={testId} className="crm-action-btn" style={{ '--accent': color }} onClick={onClick} disabled={loading}>{icon} {loading ? '...' : label}</button>);
}

function UpgradeCTA({ feature }) {
  return (<div className="crm-upgrade-cta" data-testid="crm-upgrade-cta"><Lock size={20} /><h4>Recurso indisponivel</h4><p>{feature} requer um plano superior.</p><a href="/sistema/empresa/assinatura" className="crm-btn-primary" data-testid="crm-upgrade-btn">Fazer Upgrade</a></div>);
}

// ==================== FORM TABS ====================
function ComercialTab({ crm, onSave, saving }) {
  const [form, setForm] = useState({
    responsavel_comercial: crm.responsavel_comercial || '', temperatura: crm.temperatura || 'morno',
    proxima_acao: crm.proxima_acao || '', data_followup: crm.data_followup || '',
    observacoes: crm.observacoes || '', tags: (crm.tags || []).join(', '),
    origem: crm.origem || '', whatsapp: crm.whatsapp || '',
  });
  return (
    <div className="crm-section">
      <div className="crm-form-grid">
        <label>Responsavel Comercial<input data-testid="crm-resp-comercial" value={form.responsavel_comercial} onChange={(e) => setForm({ ...form, responsavel_comercial: e.target.value })} /></label>
        <label>Temperatura<select data-testid="crm-temperatura" value={form.temperatura} onChange={(e) => setForm({ ...form, temperatura: e.target.value })}><option value="quente">Quente</option><option value="morno">Morno</option><option value="frio">Frio</option></select></label>
        <label>WhatsApp<input data-testid="crm-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></label>
        <label>Origem<input data-testid="crm-origem" value={form.origem} onChange={(e) => setForm({ ...form, origem: e.target.value })} placeholder="indicacao, organico, ads" /></label>
        <label>Proxima Acao<input data-testid="crm-proxima-acao" value={form.proxima_acao} onChange={(e) => setForm({ ...form, proxima_acao: e.target.value })} /></label>
        <label>Data Follow-up<input data-testid="crm-data-followup" type="date" value={form.data_followup} onChange={(e) => setForm({ ...form, data_followup: e.target.value })} /></label>
        <label className="crm-full-width">Tags<input data-testid="crm-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="vip, parceiro" /></label>
        <label className="crm-full-width">Observacoes<textarea data-testid="crm-observacoes" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={3} /></label>
      </div>
      <button data-testid="crm-save-comercial" className="crm-btn-primary" onClick={() => onSave({ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) })} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Comercial'}</button>
    </div>
  );
}

function FinanceiroTab({ financeiro, empresa, onSave, saving }) {
  const [form, setForm] = useState({
    status_assinatura: financeiro.status_assinatura || empresa.plan_status || '', plano: financeiro.plano || empresa.plan || 'free',
    vencimento: financeiro.vencimento || '', risco: financeiro.risco || 'baixo',
    billing_exempt: financeiro.billing_exempt ?? empresa.billing_exempt ?? false,
  });
  return (
    <div className="crm-section">
      <div className="crm-form-grid">
        <label>Status<select data-testid="crm-status-assinatura" value={form.status_assinatura} onChange={(e) => setForm({ ...form, status_assinatura: e.target.value })}><option value="">-</option><option value="active">Ativo</option><option value="trial">Trial</option><option value="expired">Expirado</option><option value="cancelled">Cancelado</option></select></label>
        <label>Plano<select data-testid="crm-plano-fin" value={form.plano} onChange={(e) => setForm({ ...form, plano: e.target.value })}><option value="free">Free</option><option value="lite">Lite</option><option value="start">Start</option><option value="gestao">Gestao</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option></select></label>
        <label>Vencimento<input data-testid="crm-vencimento" type="date" value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} /></label>
        <label>Risco<select data-testid="crm-risco" value={form.risco} onChange={(e) => setForm({ ...form, risco: e.target.value })}><option value="baixo">Baixo</option><option value="medio">Medio</option><option value="alto">Alto</option></select></label>
        <label className="crm-checkbox-label"><input data-testid="crm-billing-exempt" type="checkbox" checked={form.billing_exempt} onChange={(e) => setForm({ ...form, billing_exempt: e.target.checked })} />Isento de cobranca</label>
      </div>
      <button data-testid="crm-save-financeiro" className="crm-btn-primary" onClick={() => onSave(form)} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Financeiro'}</button>
    </div>
  );
}

// ==================== BLOCKED PAGE ====================
function CRMBlocked({ plan }) {
  return (
    <div className="crm-page crm-blocked" data-testid="crm-blocked">
      <div className="crm-blocked-inner">
        <div className="crm-blocked-icon"><Building2 size={40} /></div>
        <h2>CRM Hub</h2><p>Gestao comercial e relacionamento empresarial</p>
        <div className="crm-blocked-features">
          <div className="crm-blocked-feature">Kanban com 10 stages</div>
          <div className="crm-blocked-feature">Timeline e historico completo</div>
          <div className="crm-blocked-feature">Acoes rapidas de gestao</div>
          <div className="crm-blocked-feature">Notificacoes automaticas</div>
        </div>
        <p className="crm-blocked-plan">Seu plano atual: <b>{plan || 'free'}</b></p>
        <a href="/sistema/empresa/assinatura" className="crm-btn-primary" data-testid="crm-upgrade-btn"><Lock size={14} /> Fazer Upgrade</a>
      </div>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function AdminCRMPage() {
  const { user } = useAuth();
  const [stageData, setStageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterTemp, setFilterTemp] = useState('');
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [gates, setGates] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Load gates
  useEffect(() => {
    fetchAuth(`${API}/api/admin/crm/gates`).then(r => r.ok ? r.json() : null).then(d => { if (d) setGates(d.gates); }).catch(() => {});
  }, []);

  // Load all stages in parallel (per-stage endpoint)
  const loadAllStages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '20');
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterTemp) params.set('temperatura', filterTemp);

      const results = await Promise.all(
        STAGES.map(s => fetchAuth(`${API}/api/admin/crm/pipeline/stage/${s}?${params}`).then(r => r.ok ? r.json() : { stage: s, cards: [], total: 0, has_more: false }))
      );
      const newData = {};
      for (const r of results) {
        newData[r.stage] = { cards: r.cards, total: r.total, page: r.page || 1, has_more: r.has_more };
      }
      setStageData(newData);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [debouncedSearch, filterTemp]);

  useEffect(() => { loadAllStages(); }, [loadAllStages]);

  // Load more for a single stage
  const loadMore = async (stage) => {
    const current = stageData[stage];
    if (!current || !current.has_more) return;
    const nextPage = (current.page || 1) + 1;
    const params = new URLSearchParams();
    params.set('limit', '20');
    params.set('page', String(nextPage));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filterTemp) params.set('temperatura', filterTemp);

    try {
      const res = await fetchAuth(`${API}/api/admin/crm/pipeline/stage/${stage}?${params}`);
      if (res.ok) {
        const d = await res.json();
        setStageData(prev => ({
          ...prev,
          [stage]: {
            cards: [...(prev[stage]?.cards || []), ...d.cards],
            total: d.total,
            page: d.page,
            has_more: d.has_more,
          }
        }));
      }
    } catch {}
  };

  // Drag & drop with optimistic update + server sync
  const handleDrop = async (empresaId, newStage) => {
    // Find card in any stage
    let card = null, fromStage = null;
    for (const [s, d] of Object.entries(stageData)) {
      const found = d.cards.find(c => c.empresa_id === empresaId);
      if (found) { card = found; fromStage = s; break; }
    }
    if (!card || fromStage === newStage) return;

    // Optimistic update
    setStageData(prev => {
      const updated = { ...prev };
      updated[fromStage] = { ...prev[fromStage], cards: prev[fromStage].cards.filter(c => c.empresa_id !== empresaId), total: prev[fromStage].total - 1 };
      updated[newStage] = { ...prev[newStage], cards: [{ ...card, stage: newStage }, ...(prev[newStage]?.cards || [])], total: (prev[newStage]?.total || 0) + 1 };
      return updated;
    });

    try {
      const res = await fetchAuth(`${API}/api/admin/crm/empresa/${empresaId}/stage`, { method: 'PUT', body: JSON.stringify({ stage: newStage }) });
      if (!res.ok) loadAllStages(); // Revert on failure
    } catch { loadAllStages(); }
  };

  const totalCards = Object.values(stageData).reduce((sum, d) => sum + (d.total || 0), 0);

  // Gate check
  if (gates !== null && !gates.access && user?.role !== 'admin') return <CRMBlocked plan={user?.plan} />;

  const effectiveGates = (user?.role === 'admin' || !gates) ? {
    access: true, board: true, drawer: true, comercial: true, financeiro: true,
    timeline: true, quick_actions: true, filters_advanced: true, automations: true, notifications: true, max_timeline_events: 9999,
  } : gates;

  return (
    <div className="crm-page" data-testid="admin-crm-page">
      {/* Unified compact header */}
      <div className="crm-hub-header" data-testid="crm-header-context">
        <div className="crm-hub-header-top">
          <div className="crm-hub-count-box" data-testid="crm-total-count">
            <span className="crm-hub-count-number">{totalCards}</span>
            <span className="crm-hub-count-label">empresas</span>
          </div>
          <div className="crm-hub-controls">
            <div className="crm-hub-search" data-testid="crm-search-wrapper">
              <Search size={14} className="crm-hub-search-icon" />
              <input data-testid="crm-search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar empresa..." />
              {searchTerm && <button className="crm-hub-search-clear" onClick={() => setSearchTerm('')}><X size={13} /></button>}
            </div>
            {effectiveGates.filters_advanced && (
              <select data-testid="crm-filter-temp" className="crm-hub-filter" value={filterTemp} onChange={(e) => setFilterTemp(e.target.value)}>
                <option value="">Temp.</option>
                <option value="quente">Quente</option>
                <option value="morno">Morno</option>
                <option value="frio">Frio</option>
              </select>
            )}
            <div className="crm-hub-actions">
              {effectiveGates.notifications && <NotificationBell />}
              <button data-testid="crm-refresh" className="crm-hub-icon-btn" onClick={loadAllStages} title="Atualizar"><RefreshCw size={14} /></button>
            </div>
          </div>
        </div>
        {/* Stage trail */}
        <div className="crm-hub-trail" data-testid="crm-stats-bar">
          {STAGES.map((s) => {
            const cfg = STAGE_CONFIG[s];
            const count = stageData[s]?.total || 0;
            return (
              <div key={s} className="crm-hub-pill" data-testid={`crm-stage-pill-${s}`}>
                <span className="crm-hub-pill-dot" style={{ background: cfg.color }} />
                <span className="crm-hub-pill-label">{cfg.label}</span>
                <span className="crm-hub-pill-count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="crm-board" data-testid="crm-board">
        {loading ? (
          <div className="crm-loading">Carregando pipeline...</div>
        ) : (
          STAGES.map((s) => (
            <KanbanColumn
              key={s}
              stage={s}
              data={stageData[s] || { cards: [], total: 0, has_more: false }}
              onCardClick={(c) => setSelectedEmpresa(c.empresa_id)}
              onDrop={handleDrop}
              onLoadMore={loadMore}
              search={debouncedSearch}
              filterTemp={filterTemp}
            />
          ))
        )}
      </div>

      {selectedEmpresa && (
        <div className="crm-overlay" onClick={() => setSelectedEmpresa(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <DetailDrawer empresaId={selectedEmpresa} gates={effectiveGates} onClose={() => setSelectedEmpresa(null)} onRefresh={loadAllStages} />
          </div>
        </div>
      )}
    </div>
  );
}
